import { useQuery } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
} from '@aeris/ui'

import {
  ApiError,
  authorizeAction,
  authorizeWithCancelCode,
  fetchCancelCode,
} from '../lib/api.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'

/**
 * Um supervisor autoriza a ação de outra pessoa (ADR-0026, F034).
 *
 * "para cancelar tem que confirmar com senha" — o gerente chega ao balcão,
 * digita as credenciais dele **na sessão do operador**, e vai embora. A sessão
 * não muda: quem executa continua sendo quem estava ali, e o registro guarda
 * os dois nomes.
 *
 * Três coisas nesta tela existem por causa disso:
 *
 *   * **o motivo é obrigatório** e vem antes da senha, porque é ele que a
 *     pergunta de daqui a seis meses vai querer, e porque um gerente que digita
 *     a senha antes de saber o motivo não está autorizando, está obedecendo;
 *   * **a senha não é lembrada**, em lugar nenhum, nem por um instante depois
 *     de usada;
 *   * **a recusa é uma só.** "Senha errada", "conta desativada" e "não pode
 *     autorizar isso" chegam com o mesmo texto, porque distinguir
 *     transformaria a tela num verificador de quem é gerente na loja.
 */

export type AuthorisationDialogProps = {
  /** O que está sendo autorizado, dito para quem vai digitar a senha. */
  title: string
  description: string
  action: string
  targetType: 'sales_document' | 'cash_session' | 'party' | 'exchange'
  targetId: string
  /**
   * O que está sendo aprovado, quando a ação tem conteúdo (F148). Vai junto da
   * senha e fica gravado: a troca sem venda só conclui se for exatamente isto.
   */
  detail?: Record<string, unknown> | undefined
  /**
   * O que quem autoriza precisa ver antes da senha (F148) — a troca de fora
   * mostra as peças e deixa o admin mudar o valor aqui.
   */
  summary?: ReactNode
  /** Falso trava o Autorizar, com o motivo em `summary` (F148). */
  canAuthorise?: boolean
  /**
   * O motivo já decidido pela tela (F318): o campo some e só a senha é pedida.
   */
  fixedReason?: string | undefined
  onAuthorised: (
    authorisationId: string,
    reason: string,
    authorisedBy: string
  ) => void
  onClose: () => void
}

/** As ações que o código da loja autoriza (F363), as mesmas da API. */
const STORE_CODE_ACTIONS = [
  'sales.cancel',
  'sales.amend',
  'pos.supervise',
  'exchange.without_sale',
  'exchange.repeat_return',
  'exchange.credit.adjust',
  'finance.credit.manage',
] as const

export const AuthorisationDialog = ({
  title,
  description,
  action,
  targetType,
  targetId,
  detail,
  summary,
  canAuthorise = true,
  fixedReason,
  onAuthorised,
  onClose,
}: AuthorisationDialogProps): ReactNode => {
  const [typedReason, setReason] = useState('')
  const reason = fixedReason ?? typedReason
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  /*
   * O código da loja (F294): quando existe, quem está no balcão autoriza com
   * ele, sem chamar o gerente. Quem pediu é quem está logado. F363: vale em
   * toda autorização (editar venda paga, supervisão do PDV, trocas, crédito),
   * e não só no cancelar.
   */
  // Na 4Play (`balcao-anterior`), o código continua só para cancelar.
  const classic = useBalcaoAnterior()
  const codeAction = STORE_CODE_ACTIONS.find(
    (each) =>
      each === action &&
      (each === 'sales.cancel' || !classic)
  )
  const cancelCode = useQuery({
    queryKey: ['cancel-code'],
    queryFn: async () => await fetchCancelCode(),
    enabled: codeAction !== undefined,
  })
  const [useManager, setUseManager] = useState(false)
  const hasCode =
    codeAction !== undefined && cancelCode.data?.configured === true
  const byCode = hasCode && !useManager

  const submit = async (): Promise<void> => {
    setPending(true)
    setError(null)
    try {
      const result = byCode
        ? await authorizeWithCancelCode({
            code: password,
            action: codeAction,
            targetType,
            targetId,
            reason: reason.trim(),
            ...(detail === undefined ? {} : { detail }),
          })
        : await authorizeAction({
            email,
            password,
            action,
            targetType,
            targetId,
            reason: reason.trim(),
            ...(detail === undefined ? {} : { detail }),
          })
      // A senha sai da memória do componente antes de qualquer outra coisa.
      setPassword('')
      onAuthorised(result.authorisationId, reason.trim(), result.authorisedBy)
    } catch (failure) {
      setPassword('')
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível autorizar.'
      )
    } finally {
      setPending(false)
    }
  }

  const ready =
    canAuthorise &&
    reason.trim().length >= 3 &&
    (byCode || email !== '') &&
    password !== ''

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent title={title} description={description}>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (ready && !pending) void submit()
          }}
        >
          {/* O que está sendo aprovado, antes da senha (F148). */}
          {summary ?? null}

          {fixedReason === undefined ? (
            <Field
              label="Motivo"
              hint="Fica registrado. É o que responde a pergunta daqui a seis meses."
            >
              {(field) => (
                <Input
                  {...field}
                  value={reason}
                  autoFocus
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                />
              )}
            </Field>
          ) : null}

          {byCode ? null : (
            <Field label="E-mail de quem autoriza">
              {(field) => (
                <Input
                  {...field}
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                />
              )}
            </Field>
          )}

          <Field
            label={byCode ? 'Código da loja' : 'Senha'}
            {...(byCode
              ? {
                  hint: 'O código que o dono passou. Fica registrado que foi você.',
                }
              : {})}
          >
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="off"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
              />
            )}
          </Field>

          {hasCode ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setUseManager((current) => !current)
                setPassword('')
                setError(null)
              }}
            >
              {byCode
                ? 'Usar o e-mail e a senha do gerente'
                : 'Usar o código da loja'}
            </Button>
          ) : null}

          {error !== null && (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!ready || pending}
              leadingIcon={
                <ShieldCheck aria-hidden="true" className="h-4 w-4" />
              }
            >
              {pending ? 'Autorizando…' : 'Autorizar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
